import React, { useState, useEffect } from 'react';
import { MinerStats } from '../types';
import { apiService, formatters } from '../services/api';

interface MinerStatsPageProps {
  poolId: string;
  minerAddress: string;
}

const MinerStatsPage: React.FC<MinerStatsPageProps> = ({ poolId, minerAddress }) => {
  const [stats, setStats] = useState<MinerStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadMinerStats();
  }, [poolId, minerAddress]);

  const loadMinerStats = async () => {
    try {
      setLoading(true);
      setError(null);
      const minerStats = await apiService.getMinerStats(poolId, minerAddress);
      setStats(minerStats);
    } catch (err) {
      setError('Failed to load miner statistics');
      console.error('Error loading miner stats:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading"></div>
        <p>Loading miner statistics...</p>
      </div>
    );
  }

  if (error || !stats) {
    return (
      <div className="error-container">
        <p className="error-message">{error || 'Miner not found'}</p>
        <button onClick={loadMinerStats} className="retry-button">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="miner-stats-page">
      <h2>Miner Statistics</h2>
      <p className="miner-address">Address: {minerAddress}</p>
      
      <div className="miner-info">
        <div className="miner-stat-card">
          <div className="miner-stat-title">Current Hashrate</div>
          <div className="miner-stat-value">{formatters.hashrate(stats.hashrate)}</div>
        </div>
        
        <div className="miner-stat-card">
          <div className="miner-stat-title">Balance</div>
          <div className="miner-stat-value">{formatters.balance(stats.balance)}</div>
        </div>
        
        <div className="miner-stat-card">
          <div className="miner-stat-title">Total Paid</div>
          <div className="miner-stat-value">{formatters.balance(stats.totalPaid)}</div>
        </div>
        
        <div className="miner-stat-card">
          <div className="miner-stat-title">Last Activity</div>
          <div className="miner-stat-value">{formatters.timeAgo(stats.lastActivity)}</div>
        </div>
      </div>

      {stats.workers && stats.workers.length > 0 && (
        <div className="workers-section">
          <h3>Workers</h3>
          <div className="workers-table-container">
            <table className="workers-table">
              <thead>
                <tr>
                  <th>Worker</th>
                  <th>Hashrate</th>
                  <th>Shares</th>
                  <th>Last Seen</th>
                </tr>
              </thead>
              <tbody>
                {stats.workers.map((worker, index) => (
                  <tr key={`${worker.name}-${index}`}>
                    <td>{worker.name}</td>
                    <td>{formatters.hashrate(worker.hashrate)}</td>
                    <td>{worker.shares}</td>
                    <td>{formatters.timeAgo(worker.lastSeen)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {stats.payments && stats.payments.length > 0 && (
        <div className="payments-section">
          <h3>Recent Payments</h3>
          <div className="payments-table-container">
            <table className="payments-table">
              <thead>
                <tr>
                  <th>Amount</th>
                  <th>Transaction ID</th>
                  <th>Time</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {stats.payments.slice(0, 10).map((payment, index) => (
                  <tr key={`${payment.txId}-${index}`}>
                    <td>{formatters.balance(payment.amount)}</td>
                    <td>{payment.txId.slice(0, 16)}...{payment.txId.slice(-8)}</td>
                    <td>{formatters.timeAgo(payment.timestamp)}</td>
                    <td>
                      <span className={`status-${payment.status}`}>
                        {payment.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default MinerStatsPage;